<script setup lang="ts">
import { Head, router, useForm } from '@inertiajs/vue3';
import { FolderPlus } from 'lucide-vue-next';
import { onMounted } from 'vue';
import {
    index as projectsIndex,
    store as projectStore,
} from '@/actions/App/Http/Controllers/ProjectController';
import SectionCard from '@/components/shared/SectionCard.vue';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import AppLayout from '@/layouts/AppLayout.vue';

defineOptions({
    layout: [
        AppLayout,
        {
            breadcrumbs: [
                { title: 'Projets', href: '/projects' },
                { title: 'Nouveau projet', href: '/projects/create' },
            ],
        },
    ],
});

const form = useForm({
    name: '',
    description: '',
    status: 'active',
});

onMounted(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.get('tutorial') === '1') {
        form.name = "Impact de l'IA sur le diagnostic médical";
        form.description =
            "Revue systématique sur les applications de l'intelligence artificielle dans le diagnostic clinique (2018-2024).";
        form.status = 'active';
    }
});

const submit = () => {
    form.post(projectStore.url());
};
</script>

<template>
    <Head title="Nouveau Projet" />

    <div class="mx-auto max-w-2xl p-6">
        <div class="mb-8 flex items-center gap-4">
            <div
                class="flex h-12 w-12 items-center justify-center rounded-full bg-brand/5 text-brand"
            >
                <FolderPlus class="h-6 w-6" />
            </div>
            <div>
                <h1 class="text-2xl font-bold tracking-tight">
                    Nouveau Projet
                </h1>
                <p class="text-muted-foreground">
                    Créez une nouvelle revue de littérature.
                </p>
            </div>
        </div>

        <SectionCard class="rounded-lg shadow-sm">
            <form
                class="space-y-6"
                data-tour="project-form"
                @submit.prevent="submit"
            >
                <!-- Nom -->
                <div class="space-y-2">
                    <Label for="name">
                        Nom du projet
                        <span class="text-rose-500">*</span>
                    </Label>
                    <Input
                        id="name"
                        v-model="form.name"
                        autofocus
                        class="rounded-lg"
                        placeholder="Ex: Revue systématique sur l'IA en médecine"
                    />
                    <p v-if="form.errors.name" class="text-sm text-rose-600">
                        {{ form.errors.name }}
                    </p>
                </div>

                <!-- Description -->
                <div class="space-y-2">
                    <Label for="description">Description</Label>
                    <Textarea
                        id="description"
                        v-model="form.description"
                        :rows="4"
                        class="rounded-lg"
                        placeholder="Décrivez l'objectif de cette revue de littérature..."
                    />
                    <p
                        v-if="form.errors.description"
                        class="text-sm text-rose-600"
                    >
                        {{ form.errors.description }}
                    </p>
                </div>

                <!-- Actions -->
                <div class="flex items-center justify-end gap-3 pt-2">
                    <Button
                        type="button"
                        class="rounded-lg"
                        variant="ghost"
                        @click="router.visit(projectsIndex.url())"
                    >
                        Annuler
                    </Button>
                    <Button
                        type="submit"
                        :disabled="form.processing || !form.name"
                        class="rounded-lg bg-brand hover:bg-brand/90"
                    >
                        <FolderPlus class="mr-2 h-4 w-4" />
                        Créer le projet
                    </Button>
                </div>
            </form>
        </SectionCard>
    </div>
</template>
